import React, { useRef, useState, useEffect } from "react";
import { Icon, Tooltip } from "@mui/material";
import {
  OwcButton,
  OwcChip,
  OwcEditableDropdown,
  OwcIconButton,
  OwcInput,
  OwcListItem,
  OwcTypography,
} from "@one/react";
import { find } from "lodash-es";
import {
  DEFAULT_COLUMNS,
  dropDownSelectionSource,
  MY_LAB,
  SOP_RT,
  TOOL_PANELS,
  helpTextColumns,
  helpTextFilters,
} from "../Constants/constants";
import CircleHelpFilled from "../icons/CircleHelpFilled";
import { isNull } from "lodash-es";

export const GridAutoCompleteStyles = {
  gridAutoCompleteLableShrink: {
    "& .MuiInputLabel-filled.MuiInputLabel-shrink": {
      transform: "translate(12px, 4px) scale(0.75)",
    },
  },
};

export const generateID = {
  UUID(moduleName, UUID, elementType) {
    let returner = `${moduleName}_${UUID}`;
    if (elementType) {
      returner = `${elementType}_${returner}`;
    }
    return returner;
  },
};

const SaveConfiguration = ({
  defaultShowColumns,
  defaultFilterObj,
  savedChips = [],
  currentSideBarPanel,
  savedChip,
  onApplyColumns,
  onApplyFilter,
  onSave,
  onChipDelete,
  sideBarPanels,
  onFilterSourceSelection,
  gridRef,
  storeUser
}) => {
  const [newValue, setNewValue] = useState("");
  const [existingValue, setExistingValue] = useState(null);
  const [lastSavedChip, setLastSavedChip] = useState({});
  const [selected, setSelected] = useState(null);
  const [chips, setChips] = useState([]);
  const [options, setOptions] = useState([]);
  const [pannel, setPannel] = useState();
  const chipElement = useRef(null);

  const onChipCloseIconClick = async (index) => {
    let result = null;
    if (sideBarPanels.includes(TOOL_PANELS.filters)) {
      result = await onChipDelete(chips[index], lastSavedChip, TOOL_PANELS.filters, chips);
    } else {
      result = await onChipDelete(chips[index], lastSavedChip, TOOL_PANELS.columns, chips);
    }
    if (result?.userFilters) {
      const resultList = [...result.userFilters];
      if (result?.panel === TOOL_PANELS.filters && find(resultList, { filterName: MY_LAB })) {
        resultList.shift();
      }
      if (result?.panel === TOOL_PANELS.columns && find(resultList, { displayName: SOP_RT })) {
        resultList.shift();
      }
      setOptions(() => [...resultList]);
    }
  };

  const onColumnStateApply = (chipObj, isDefaultColumns) => {
    onApplyColumns(isDefaultColumns ? defaultShowColumns : chipObj);
    if (onSave) {
      onSave({
        currentState: gridRef?.current?.columnApi?.getColumnState(),
        chipName: isDefaultColumns ? DEFAULT_COLUMNS().displayName : chipObj?.chipName,
        pannel: TOOL_PANELS.columns,
      });
    }
  };

  const onFilterApply = (chipObj, isDefaultFilter) => {
    const jsonObj =
      typeof chipObj?.filterDefinition === "string" ? JSON.parse(chipObj?.filterDefinition) : chipObj?.filterDefinition;
    if (onSave) {
      onSave({
        currentState: isDefaultFilter ? defaultFilterObj?.filterDefinition : jsonObj,
        chipName: isDefaultFilter ? defaultFilterObj?.filterName : chipObj?.chipName,
        pannel: TOOL_PANELS.filters,
      });
    }
    onApplyFilter(isDefaultFilter ? defaultFilterObj?.filterDefinition : jsonObj);
  };

  const onChipClicked = (index) => {
    const chipObj = chips[index];
    let isSameChipClicked = false;
    setLastSavedChip((prevState) => {
      const savedChip =
        prevState?.chipName === chipObj?.chipName
          ? {}
          : {
              ...chipObj,
            };
      isSameChipClicked = prevState?.chipName === chipObj?.chipName;
      if (sideBarPanels.includes(TOOL_PANELS.filters)) {
        if (onFilterSourceSelection) {
          onFilterSourceSelection(dropDownSelectionSource?.agGridFilter);
        }
        onFilterApply(chipObj, isSameChipClicked);
      } else if (sideBarPanels.includes(TOOL_PANELS.columns)) {
        if (onFilterSourceSelection) {
          onFilterSourceSelection(dropDownSelectionSource?.agGridColumn);
        }
        onColumnStateApply(chipObj, isSameChipClicked);
      }
      return savedChip;
    });
  };

  useEffect(() => {
    if (gridRef && gridRef?.current?.api?.getSideBar()) {
      const panels = gridRef && gridRef?.current?.api?.getSideBar();
      const tempArr = panels?.toolPanels?.map((panel) => panel?.id);
      if (tempArr?.includes(TOOL_PANELS.filters)) {
        setPannel(() => TOOL_PANELS.filters);
      } else if (tempArr?.includes(TOOL_PANELS.columns)) {
        setPannel(() => TOOL_PANELS.columns);
      }

      let tempChips = [...savedChips];
      if (tempArr?.includes(TOOL_PANELS.filters) && find(tempChips, { filterName: MY_LAB })) {
        tempChips.shift();
      }
      if (tempArr?.includes(TOOL_PANELS.columns) && find(tempChips, { chipName: SOP_RT })) {
        tempChips.shift();
      }
      tempChips = tempChips.filter((chip) => chip.userID === storeUser?.id || (storeUser?.roles  || []).some((role) => role === 'BookingAdmin'));
      setOptions(tempChips);
      setChips([...savedChips]);
      setLastSavedChip({
        ...savedChip,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSideBarPanel]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          borderBottom: "solid 1px black",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px",
        }}
      >
        <OwcTypography variant="subtitle1">
          Saved {pannel === TOOL_PANELS.filters ? `filters` : `columns`}
        </OwcTypography>
        <Tooltip
          data-testid="icon-tooltip"
          title={pannel === TOOL_PANELS.filters ? helpTextFilters?.savedFilters : helpTextColumns?.savedColumns}
          placement="left"
        >
          <Icon>
            <CircleHelpFilled></CircleHelpFilled>
          </Icon>
        </Tooltip>
      </div>
      <div style={{ minHeight: 200 }}>
        <div
          style={{
            display: "flex",
            padding: 10,
            flexWrap: "wrap",
          }}
        >
          {chips.map((data, index) => {
            const isSelected = lastSavedChip?.chipName === data.chipName;
            const isRemovable = ![SOP_RT, MY_LAB].includes(data.chipName) && !data.sharedWithGroups?.length;

            return (
              <div key={index} style={{ marginBottom: 10 }}>
                <OwcChip
                  ref={chipElement}
                  outlined
                  selected={isSelected}
                  removable={isRemovable}
                  onClick={() => onChipClicked(index)}
                  onRemove={async () => onChipCloseIconClick(index)}
                  style={{ marginRight: "10px" }}
                >
                  {data.chipName}
                </OwcChip>
              </div>
            );
          })}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          borderTop: "solid 1px black",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px",
        }}
      >
        <OwcTypography variant="subtitle1">
          Save current {pannel === TOOL_PANELS.filters ? `filters` : `columns`}
        </OwcTypography>
        <Tooltip
          data-testid="icon-tooltip"
          title={
            pannel === TOOL_PANELS.filters ? helpTextFilters?.saveCurrentFilters : helpTextColumns?.saveCurrentColumns
          }
          placement="left"
        >
          <Icon>
            <CircleHelpFilled></CircleHelpFilled>
          </Icon>
        </Tooltip>
      </div>
      <div style={{ padding: "10px" }}>
        <OwcEditableDropdown
          label="Select existing configuration"
          placeholder="Select existing configuration"
          value={selected ? selected?.chipName : null}
          onSelectChange={(e) => {
            setSelected(() => (!isNull(e.detail) ? options[e.detail] : e.detail));
            setExistingValue(() => (!isNull(e.detail) ? options[e.detail] : e.detail));
          }}
          disabled={newValue !== ""}
        >
          {options?.map((item) => {
            const option = item;
            return (
              <>
                <OwcListItem
                  value={option?.chipName}
                  key={option?.chipName}
                  data-testid={`${option?.chipName}`}
                  id={generateID.UUID(`DlabEditableDD-${option?.chipName}`, 1, "item")}
                >
                  {option?.chipName}
                </OwcListItem>
              </>
            );
          })}
        </OwcEditableDropdown>
      </div>
      <div style={{ padding: "10px" }}>
        <OwcInput
          data-testid={`text-field-filter-column`}
          label="New configuration name"
          name="filter"
          value={newValue}
          onInputChange={(event) => {
            setNewValue(event?.target?.value?.trim() ? event?.target?.value : event?.target?.value?.trim());
          }}
          disabled={existingValue !== null}
        >
          {newValue?.length > 0 && (
            <OwcIconButton
              data-testid={`filter-input-clear-button`}
              slot="suffix"
              onClick={() => {
                setNewValue("");
              }}
              flat
              icon="circle_clear"
              type="filled"
            />
          )}
        </OwcInput>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          paddingRight: "10px",
        }}
      >
        <OwcButton
          disabled={
            !(newValue || existingValue) ||
            existingValue?.chipName === "SOP RT0020" ||
            existingValue?.chipName === MY_LAB
          }
          onClick={async () => {
            const obj = await onSave({
              currentState: sideBarPanels.includes(TOOL_PANELS.filters)
                ? gridRef.current.api.getFilterModel()
                : gridRef.current.columnApi.getColumnState(),
              chipName: existingValue ? existingValue?.chipName : newValue,
              type: existingValue ? "update" : "create",
              isAgGridObj: true,
              pannel: sideBarPanels.includes(TOOL_PANELS.filters) ? TOOL_PANELS.filters : TOOL_PANELS.columns,
              isSaveConfigUpdated: true,
            });
            if (obj?.result) {
              if (!existingValue) {
                setOptions((prevState) => [...prevState, obj?.savedObj]);
              }
              setNewValue(() => "");
              setChips((prevState) => {
                let updatedChips = [];
                if (existingValue) {
                  updatedChips = prevState?.map((chip) => {
                    if (chip?.chipName === existingValue?.chipName) {
                      if (sideBarPanels.includes(TOOL_PANELS.filters)) {
                        chip.filterDefinition = obj?.savedObj?.filterDefinition;
                      } else {
                        chip.displayDefinition = JSON.stringify(obj?.savedObj?.displayDefinition);
                      }
                    }
                    return chip;
                  });
                } else {
                  let chipObj = {
                    chipName: obj?.savedObj?.chipName,
                    userID: obj?.savedObj?.userID,
                  };
                  const columnObj = {
                    displayDefinition: JSON.stringify(obj?.savedObj?.displayDefinition),
                    displayName: obj?.savedObj?.chipName,
                  };
                  const filterObj = {
                    filterDefinition: obj?.savedObj?.filterDefinition,
                    filterName: obj?.savedObj?.chipName,
                  };
                  if (sideBarPanels.includes(TOOL_PANELS.filters)) {
                    chipObj = {
                      ...chipObj,
                      ...filterObj,
                    };
                  } else {
                    chipObj = {
                      ...chipObj,
                      ...columnObj,
                    };
                  }
                  updatedChips = [...prevState, chipObj];
                }
                return updatedChips;
              });

              setLastSavedChip(() => {
                let chipObj = {};

                if (existingValue) {
                  chipObj = sideBarPanels.includes(TOOL_PANELS.filters)
                    ? {
                        ...existingValue,
                        filterDefinition: obj?.savedObj?.filterDefinition,
                      }
                    : {
                        ...existingValue,
                        displayDefinition: obj?.savedObj?.displayDefinition,
                      };
                } else {
                  chipObj = {
                    chipName: obj?.savedObj?.chipName,
                    userID: obj?.savedObj?.userID,
                  };
                  const columnObj = {
                    displayDefinition: JSON.stringify(obj?.savedObj?.displayDefinition),
                    displayName: obj?.savedObj?.chipName,
                  };
                  const filterObj = {
                    filterDefinition: obj?.savedObj?.filterDefinition,
                    filterName: obj?.savedObj?.chipName,
                  };
                  if (sideBarPanels.includes(TOOL_PANELS.filters)) {
                    chipObj = {
                      ...chipObj,
                      ...filterObj,
                    };
                  } else {
                    chipObj = {
                      ...chipObj,
                      ...columnObj,
                    };
                  }
                }

                return chipObj;
              });
            }
          }}
        >
          Save
        </OwcButton>
      </div>
    </div>
  );
};

export default SaveConfiguration;
